import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, FileText, Mail } from 'lucide-react';

const Privacy = () => {
  const sections = [
    {
      title: "Information We Collect",
      icon: Database,
      content: `We collect information you provide directly to us, such as when you create an account, use our services, or contact us. This includes your name, email address, company information, and any other information you choose to provide. We also automatically collect certain information about your device and usage of our service.`
    },
    {
      title: "How We Use Your Information",
      icon: Eye,
      content: `We use the information we collect to provide, maintain, and improve our services; process transactions; send technical notices and support messages; respond to your comments and questions; and communicate with you about products, services, and events. We may also use your information to prevent fraud and enhance security.`
    },
    {
      title: "Information Sharing",
      icon: Lock,
      content: `We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information with service providers who assist us in operating our platform, conducting business, or serving users, provided they agree to keep this information confidential.`
    },
    {
      title: "Data Security",
      icon: Shield,
      content: `We implement enterprise-grade security measures to protect your information. This includes encryption, access controls, regular security audits, and enterprise-grade security standards. However, no method of transmission over the Internet or electronic storage is 100% secure.`
    },
    {
      title: "Data Retention",
      icon: FileText,
      content: `We retain your information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your personal information at any time.`
    },
    {
      title: "Your Rights",
      icon: Eye,
      content: `You have the right to access, update, or delete your personal information. You may also opt out of certain communications and request that we limit the use of your information. For users in the EU/EEA, you have additional rights under GDPR, including the right to data portability and the right to lodge a complaint with a supervisory authority.`
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full p-4 mr-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900">
                Privacy Policy
              </h1>
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8"
            >
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 inline-block"
            >
              <div className="flex items-center space-x-4">
                <Shield className="h-6 w-6 text-green-600" />
                <span className="font-semibold text-gray-900">Enterprise Security Standards</span>
                <div className="w-1 h-6 bg-gray-300"></div>
                <Lock className="h-6 w-6 text-blue-600" />
                <span className="font-semibold text-gray-900">Advanced Encryption</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Your Privacy</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                At Aquargin, we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
                enterprise automation platform and related services.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This policy applies to all users of our services, including enterprise clients, individual users, and visitors 
                to our website. By using our services, you consent to the data practices described in this policy.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Sections */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-8 shadow-lg"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-3 flex-shrink-0">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                          {section.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {section.content}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* GDPR Compliance */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 rounded-full p-3 mr-4">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">GDPR & International Compliance</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">European Users</h3>
                  <p className="text-gray-700 leading-relaxed">
                    For users in the European Union and European Economic Area, we comply with the General Data Protection 
                    Regulation (GDPR). You have enhanced rights including data portability, the right to be forgotten, 
                    and the right to lodge complaints with supervisory authorities.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Global Standards</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We maintain the same high standards of data protection for all users regardless of location. 
                    Our practices are designed to meet or exceed international privacy regulations including CCPA, 
                    PIPEDA, and other applicable data protection laws.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact & Updates */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Mail className="h-12 w-12 text-white mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Questions About Your Privacy?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Our Data Protection Officer is here to help with any privacy-related questions or concerns.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="mailto:privacy@aquargin.com"
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300"
              >
                Contact Privacy Team
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300"
              >
                General Contact
              </a>
            </div>

            <div className="text-white/80 text-sm">
              <p className="mb-2">Last updated: January 1, 2025</p>
              <p>We will notify you of any material changes to this Privacy Policy via email or through our platform.</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy; 